import { createContext, useState, useEffect } from 'react';
import type { FC } from 'react';
import { Activity } from 'src/types/activity';
import { ContextProviderProps } from 'src/types/contextProviderProps';
import useSettings from 'src/hooks/useSettings';
import moment from 'moment';
import useAuth from 'src/hooks/useAuth';
import crmApi from 'src/api/CrmApi';
import { LOCAL_STORAGE_KEYS } from 'src/constants';
import { getWithExpiry, setWithExpiry } from 'src/utils/localStorageUtils';

interface NotificationsContext {
  isNotificationDrawerOpen?: boolean;
  bellTotal: number;
  tasksInNotificationRange?: Activity[];
  openNotificationDrawer?: () => void;
  closeNotificationDrawer?: () => void;

  /**
   * Reload the tasks used for notifications, this will populate the bellTotal and the tasks that trigger the modal
   * @returns void
   */
  reloadTaskNotifications?: () => void;
  setHasModalAlreadyOpened?: (hasModalAlreadyOpened: boolean) => void;
  hasModalAlreadyOpened?: boolean;

  /**
   * Set notifications for 24 hours from right now
   */
  pauseFor24Hours?: () => void;

  /**
   * The date/time that notifications are paused until
   */
  isNotificationPaused?: () => boolean;
}

const NotificationContext = createContext<NotificationsContext>({
  isNotificationDrawerOpen: false,
  bellTotal: 0,
  tasksInNotificationRange: [],
  reloadTaskNotifications: () => {},
  openNotificationDrawer: () => {},
  closeNotificationDrawer: () => {},
  setHasModalAlreadyOpened: () => {},
  hasModalAlreadyOpened: false,
  pauseFor24Hours: () => {},
  isNotificationPaused: () => false,
});

export const NotificationProvider: FC<ContextProviderProps> = (props) => {
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState<boolean>(false);
  const [bellTotal, setBellTotal] = useState<number>(0);
  const [tasksInNotificationRange, setTasksInNotificationRange] = useState<Activity[]>([]);
  const [hasModalAlreadyOpened, setHasModalAlreadyOpened] = useState<boolean>(false);
  const notificationDays = useSettings((state) => state.settings.notificationDays);
  const { user } = useAuth();

  //Load task notifications based on settings
  const reloadTaskNotifications = (): void => {
    console.log('reloadTaskNotifications - notificationsContext');
    if (!user?.id || !notificationDays) {
      return;
    }

    crmApi
      .getTasksForNotifications(user.id, notificationDays)
      .then((tasks) => {
        if (tasks?.length > 0) {
          const rangeEnd = moment().add(notificationDays, 'days').endOf('day');
          const filteredTasks = tasks.filter((t) => {
            return t.dueDate !== null && moment(t.dueDate).isBefore(rangeEnd) && t.assignedTo === user.id;
          });
          setTasksInNotificationRange(filteredTasks);

          setBellTotal(filteredTasks.length);
        } else {
          setTasksInNotificationRange([]);
          setBellTotal(0);
        }
      })
      .catch((err) => {
        console.error('reloadTaskNotifications - notificationsContext err', err);
      });
  };

  const openNotificationDrawer = (): void => {
    setIsNotificationDrawerOpen(true);
  };

  const closeNotificationDrawer = (): void => {
    setIsNotificationDrawerOpen(false);
  };

  const pauseFor24Hours = (): void => {
    setWithExpiry(LOCAL_STORAGE_KEYS.NotificationsArePaused, true, 60 * 24);
  };

  const isNotificationPaused = (): boolean => {
    const paused = getWithExpiry<boolean | null>(LOCAL_STORAGE_KEYS.NotificationsArePaused);
    return paused ?? false;
  };

  //Whenever settings change, or user changes reload tasks
  useEffect(() => {
    reloadTaskNotifications();
  }, [user?.id, notificationDays]);

  const contextValue = {
    isNotificationDrawerOpen,
    bellTotal,
    tasksInNotificationRange,
    reloadTaskNotifications,
    openNotificationDrawer,
    closeNotificationDrawer,
    setHasModalAlreadyOpened,
    hasModalAlreadyOpened,
    pauseFor24Hours,
    isNotificationPaused,
  };

  return <NotificationContext.Provider value={contextValue}>{props.children}</NotificationContext.Provider>;
};

export default NotificationContext;
